import {removeFormFromLocation} from "./actions/removeFormFromLocation";
import {createAction, createSlice} from "@reduxjs/toolkit";
import {Location} from "../../@types/location";
import {Template} from "../../@types/template";
import {TemplateQuestion} from "../../@types/templateQuestion";
import {UserRole} from "../../@types/userRole";
import {assignFormToLocation} from "./actions/assignFormToLocation";
import {assignFormToUser} from "./actions/assignFormToUser";
import {findAssignedLocations} from "./actions/findAssignedLocations";
import {findAssignedUsers} from "./actions/findAssignedUsers";
import {findLocations} from "./actions/findLocations";
import {findTemplate} from "./actions/findTemplate";
import {findTemplateQuestions} from "./actions/findTemplateQuestions";
import {findUserRoles} from "./actions/findUserRoles";
import {removeQuestionFromList} from "./actions/removeQuestionFromList";
import {updateTemplateData} from "./actions/updateTemplateData";
import {removeFormFromUser} from "./actions/removeFormFromUser";
import {Organization} from "../../@types/organization";
import {Category} from "../../@types/Category";
import {SignatureProvider} from "../../@types/signatureProvider";
import {User} from "../../@types/user";
import {updateFileTemplate} from "../templateCreate/actions/updateFileTemplate";

interface TemplateDetailState {
  detail: null,
  template: Template;
  questionList: TemplateQuestion[];
  findTemplateStatus: string;
  findQuestionsStatus: string;
  segmentSelected: string;
  createQuestionsStatus: string;
  updateTemplateStatus: string;
  locationsSearchText: string;
  locationList: Location[];
  userRoleList: UserRole[];
  userRoleSearchText: string;
  assignFormStatus: string;
  locationsAssigned: Location[];
  usersAssigned: UserRole[];
  findLocationsStatus: string;
  findUsersStatus: string;
  locationsFilter: {
    partOf: Location[];
  };
  partOfFilterVisible: boolean;
  usersFilter: {
    location: Location;
    organization: Organization;
  };
}

const initialState = {
  detail: null,
  template: {} as Template,
  questionList: [],
  findTemplateStatus: "",
  findQuestionsStatus: "",
  segmentSelected: "preview",
  createQuestionsStatus: "",
  updateTemplateStatus: "",
  locationsSearchText: "",
  locationList: [],
  userRoleList: [],
  userRoleSearchText: "",
  assignFormStatus: "",
  locationsAssigned: [],
  usersAssigned: [],
  findLocationsStatus: "",
  findUsersStatus: "",
  locationsFilter: {
    partOf: [],
  },
  partOfFilterVisible: false,
  usersFilter: {
    location: {} as Location,
    organization: {} as Organization,
  },
} as TemplateDetailState;

//
export const setSegmentSelected = createAction<string>("SET_SEGMENT_TEMPLATE_DETAIL");
export const addQuestionToList = createAction<TemplateQuestion>("ADD_QUESTION_TO_LIST_DETAIL");
export const setQuestionType = createAction<TemplateQuestion>("SET_QUESTION_DETAIL_TYPE");
export const setQuestionLabel = createAction<TemplateQuestion>("SET_QUESTION_DETAIL_LABEL");
export const setTemplateText = createAction<string>("DETAIL_SET_TEMPLATE_TEXT");
export const removeFromList = createAction<number>("REMOVE_QUESTION_FROM_LIST");
export const syncReorderList = createAction<{ from: number; to: number }>("REORDER_QUESTIONS_LIST");
export const setUpdateTemplateStatus = createAction<string>("SET_UPDATE_TEMPLATE_STATUS");
export const setTemplateAssignedTo = createAction<string>("DETAIL_SET_TEMPLATE_ASSIGNED_TO");
export const setLocationsSearchText = createAction<string>("setLocationsSearchText");
export const setCheckLocation = createAction<Location>("templateDetailSetCheckLocation");
export const setCheckAllLocation = createAction<Location[]>("templateDetailSetCheckAllLocation");
export const setUncheckAllLocation = createAction<Location[]>(
  "templateDetailSetUncheckAllLocation"
);
export const setUserRoleSearchText = createAction<string>("setUserRoleSearchText");
export const setCheckUserRole = createAction<UserRole>("templateDetailSetCheckUserRole");
export const setCheckAllUserRole = createAction<UserRole[]>("templateDetailSetCheckAllUserRole");
export const setUncheckAllUserRole = createAction<UserRole[]>(
  "templateDetailSetUncheckAllUserRole"
);
export const setAssignFormStatus = createAction<string>("setAssignFormStatus");
export const setPartOfFilterVisible = createAction<boolean>("setTemplateDetailPartOfFilterVisible");
export const setPartOfFilter = createAction<Location[]>("setTemplateDetailPartOfFilter");
export const setLocation = createAction<Location>("templateDetailSetLocationFilter");
export const setOrganization = createAction<Organization>("templateDetailSetOrganizationFilter");
export const clearUsersFilters = createAction("templateDetailClearUsersFilters");
export const clearLocationsFilters = createAction("templateDetailClearLocationsFilters");

const templateDetailSlice = createSlice({
  name: "templateDetail",
  initialState,
  reducers: {
    setQuestionFile(state, {
      payload: {
        order: orderInList,
        category: category,
        label: label,
        template: template,
        signatureProvider: signatureProvider,
        legalRepresentative: legalRepresentative,
      }
    }) {
      const question = state.questionList.find(({order}) => order === orderInList);
      if (question) {
        if (label) {
          question.label = label;
        }
        if (category) {
          question.category = category as Category;
        }
        if (template) {
          question.template = template as Template;
        }
        if (signatureProvider) {
          question.signatureProvider = signatureProvider as SignatureProvider;
        }
        if (legalRepresentative) {
          question.legalRepresentative = legalRepresentative as User;
        }

      }

    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(findTemplate.pending, (state) => {
        state.findTemplateStatus = "pending";
        state.template = {} as Template;
      })
      .addCase(findTemplate.rejected, (state) => {
        state.findTemplateStatus = "rejected";
      })
      .addCase(findTemplate.fulfilled, (state, {payload}) => {
        state.template = payload;
        state.findTemplateStatus = "fulfilled";
      })
      .addCase(findTemplateQuestions.pending, (state) => {
        state.findQuestionsStatus = "pending";
      })
      .addCase(findTemplateQuestions.rejected, (state) => {
        state.findQuestionsStatus = "rejected";
      })
      .addCase(findTemplateQuestions.fulfilled, (state, {payload}) => {
        state.questionList = payload;
        state.findQuestionsStatus = "fulfilled";
      })
      .addCase(setSegmentSelected, (state, {payload}) => {
        state.segmentSelected = payload;
      })
      .addCase(addQuestionToList, (state, {payload}) => {
        state.questionList.push(payload);
      })
      .addCase(setQuestionType, (state, {payload: {order: payloadOrder, type}}) => {
        const question = state.questionList.find(({order}) => order === payloadOrder);
        if (question) {
          question.type = type;
        }
      })
      .addCase(setQuestionLabel, (state, {payload: {order: payloadOrder, label}}) => {
        const question = state.questionList.find(({order}) => order === payloadOrder);
        if (question) {
          question.label = label;
        }
      })
      .addCase(setUpdateTemplateStatus, (state, {payload}) => {
        state.updateTemplateStatus = payload;
      })
      .addCase(updateTemplateData.pending, (state) => {
        state.updateTemplateStatus = "pending";
      })
      .addCase(updateTemplateData.rejected, (state) => {
        state.updateTemplateStatus = "rejected";
      })
      .addCase(updateTemplateData.fulfilled, (state, {payload}) => {
        state.updateTemplateStatus = "fulfilled";
        state.template = payload.templateResponse;
        state.questionList = payload.questionResponseList;
      })
      .addCase(setTemplateText, (state, {payload}) => {
        state.template.richText = payload;
      })
      .addCase(removeFromList, (state, {payload}) => {
        state.questionList = state.questionList
          .filter(({order}) => order !== payload)
          .map((question) => {
            if (payload && question.order) {
              return {
                ...question,
                order: question.order > payload ? question.order - 1 : question.order,
              };
            } else {
              return {} as TemplateQuestion;
            }
          });
      })
      .addCase(removeQuestionFromList.fulfilled, (state, {payload}) => {
        const {order: payloadOrder} = payload;

        state.questionList = state.questionList
          .filter(({order}) => order !== payloadOrder)
          .map((question) => {
            if (payloadOrder && question.order) {
              return {
                ...question,
                order: question.order > payloadOrder ? question.order - 1 : question.order,
              };
            } else {
              return {} as TemplateQuestion;
            }
          });
      })
      .addCase(setTemplateAssignedTo, (state, {payload}) => {
        state.template.assignedTo = payload;
      })
      .addCase(setLocationsSearchText, (state, {payload}) => {
        state.locationsSearchText = payload;
      })
      // findLocationForms
      .addCase(findLocations.pending, (state, {payload}) => {
        state.locationList = [];
        state.assignFormStatus = "";
        state.userRoleList = [];
        state.findLocationsStatus = "pending";
        state.findUsersStatus = "";
        state.locationsFilter = {partOf: []};
        state.partOfFilterVisible = false;
      })
      .addCase(findLocations.fulfilled, (state, {payload}) => {
        const locationList = payload.map((location: Location) => ({
          ...location,
          checked: false,
        }));

        state.locationList = locationList;
        state.findLocationsStatus = "fulfilled";
      })
      .addCase(setCheckLocation, (state, {payload}) => {
        state.locationList = state.locationList.map((locationItem) => ({
          ...locationItem,
          checked: payload.id === locationItem.id ? !locationItem.checked : locationItem.checked,
        }));
      })
      .addCase(setCheckAllLocation, (state, {payload}) => {
        state.locationList = state.locationList.map((locationItem) => ({
          ...locationItem,
          checked: payload.some(({id}) => locationItem.id === id) ? true : locationItem.checked,
        }));
      })
      .addCase(setUncheckAllLocation, (state, {payload}) => {
        state.locationList = state.locationList.map((locationItem) => ({
          ...locationItem,
          checked: payload.some(({id}) => locationItem.id === id) ? false : locationItem.checked,
        }));
      })
      .addCase(assignFormToLocation.fulfilled, (state, {payload}) => {
        state.locationList = state.locationList.filter(({id}) => id !== payload.location.id);
        state.locationsAssigned.push(payload.location);
      })
      .addCase(removeFormFromLocation.fulfilled, (state, {payload}) => {
        state.locationsAssigned = state.locationsAssigned.filter(
          ({id}) => id !== payload.location.id
        );
        state.locationList.push(payload.location);
      })
      // findLocationUserRoles
      .addCase(findUserRoles.pending, (state, {payload}) => {
        state.userRoleList = [];
        state.assignFormStatus = "";
        state.findUsersStatus = "pending";
        state.findLocationsStatus = "";
      })
      .addCase(findUserRoles.fulfilled, (state, {payload}) => {
        const userRoleList = payload.map((userRole: UserRole) => ({
          ...userRole,
          checked: false,
        }));

        state.userRoleList = userRoleList;
        state.findUsersStatus = "fulfilled";
      })
      .addCase(setCheckUserRole, (state, {payload}) => {
        state.userRoleList = state.userRoleList.map((userRoleItem) => ({
          ...userRoleItem,
          checked: payload.id === userRoleItem.id ? !userRoleItem.checked : userRoleItem.checked,
        }));
      })
      .addCase(setCheckAllUserRole, (state, {payload}) => {
        state.userRoleList = state.userRoleList.map((userRoleItem) => ({
          ...userRoleItem,
          checked: payload.some(({id}) => userRoleItem.id === id) ? true : userRoleItem.checked,
        }));
      })
      .addCase(setUncheckAllUserRole, (state, {payload}) => {
        state.userRoleList = state.userRoleList.map((userRoleItem) => ({
          ...userRoleItem,
          checked: payload.some(({id}) => userRoleItem.id === id) ? false : userRoleItem.checked,
        }));
      })
      .addCase(setUserRoleSearchText, (state, {payload}) => {
        state.userRoleSearchText = payload;
      })
      .addCase(removeFormFromUser.fulfilled, (state, {payload}) => {
        state.usersAssigned = state.usersAssigned.filter(({user}) => user.id !== payload.user.id);
        state.userRoleList.push(payload);
      })
      .addCase(assignFormToUser.fulfilled, (state, {payload}) => {
        state.userRoleList = state.userRoleList.filter(({user}) => user.id !== payload.user.id);
        state.usersAssigned.push(payload);
      })

      .addCase(setAssignFormStatus, (state, {payload}) => {
        state.assignFormStatus = payload;
      })
      .addCase(findAssignedLocations.fulfilled, (state, {payload}) => {
        state.locationsAssigned = payload;
      })
      .addCase(findAssignedUsers.fulfilled, (state, {payload}) => {
        state.usersAssigned = payload;
      })
      // Locations Filter
      .addCase(setPartOfFilterVisible, (state, {payload}) => {
        state.partOfFilterVisible = payload;
      })
      .addCase(setPartOfFilter, (state, {payload}) => {
        state.locationsFilter.partOf = payload;
      })
      .addCase(clearLocationsFilters, (state) => {
        state.locationsFilter = {
          partOf: [],
        };
      })
      // Users Filters
      .addCase(setLocation, (state, {payload}) => {
        state.usersFilter.location = payload;
      })
      .addCase(setOrganization, (state, {payload}) => {
        state.usersFilter.organization = payload;
      })
      .addCase(clearUsersFilters, (state) => {
        state.usersFilter = {
          location: {} as Location,
          organization: {} as Organization,
        };
      })
      .addCase(updateFileTemplate.pending, (state) => {
        state.updateTemplateStatus = "pending";
      })
      .addCase(updateFileTemplate.rejected, (state) => {
        state.updateTemplateStatus = "rejected";
      })
      .addCase(updateFileTemplate.fulfilled, (state, action) => {
        const updated = action.payload as Template; // el backend debe devolver la plantilla completa
        state.updateTemplateStatus = "fulfilled";
        // Si el detalle cargado corresponde al mismo id, actualízalo
        if (state.template && state.template.id === updated.id) {
          state.template = updated;
        }
      })
  },
});

export const {setQuestionFile} = templateDetailSlice.actions;

export default templateDetailSlice.reducer;
